'use client';

import { createAuthClient } from 'better-auth/react';
import { jwtClient } from 'better-auth/client/plugins';
import type { ReactNode } from 'react';
import { env } from '@/env';
import { createFreshAccessTokenGetter } from './session-token';

export const authClient = createAuthClient({
	baseURL: new URL(env.NEXT_PUBLIC_API_URL).origin,
	basePath: '/api/v1/auth',
	plugins: [jwtClient()],
	fetchOptions: { credentials: 'include' },
});

type ChildrenProps = { children?: ReactNode };

const getFreshAccessToken = createFreshAccessTokenGetter(async () => {
	const result = await authClient.token();
	return result.data ? { accessToken: result.data.token } : null;
});

function redirectToSignIn(callbackUrl?: string) {
	const url = new URL('/sign-in', window.location.origin);
	if (callbackUrl) url.searchParams.set('callbackURL', callbackUrl);
	window.location.assign(url.toString());
}

async function endBrowserAndIdentitySession() {
	await authClient.signOut();
	window.location.assign('/');
}

export function useAuth() {
	const { data, isPending } = authClient.useSession();
	return {
		isLoaded: !isPending,
		isSignedIn: !!data?.user,
		isAdmin: (data?.user as { isAdmin?: boolean } | undefined)?.isAdmin === true,
		userId: data?.user?.id ?? null,
		getToken: getFreshAccessToken,
	};
}

export function useUser(): { isLoaded: boolean; isSignedIn: boolean; user: any } {
	const { data, isPending } = authClient.useSession();
	const nameParts = data?.user?.name?.trim().split(/\s+/, 2) ?? [];
	return {
		isLoaded: !isPending,
		isSignedIn: !!data?.user,
		user: data?.user
			? {
					id: data.user.id,
					firstName: nameParts[0] ?? '',
					lastName: nameParts[1] ?? '',
					fullName: data.user.name || data.user.email,
					imageUrl: data.user.image ?? '',
					primaryEmailAddress: {
						emailAddress: data.user.email,
						verification: { status: data.user.emailVerified ? 'verified' : 'unverified' },
					},
				}
			: null,
	};
}

export function useClerk() {
	return {
		openSignIn: ({ forceRedirectUrl }: { forceRedirectUrl?: string } = {}) =>
			redirectToSignIn(forceRedirectUrl),
		signOut: endBrowserAndIdentitySession,
	};
}

export function useSession(): { session: any; isLoaded: boolean } {
	const { data, isPending } = authClient.useSession();
	return {
		session: data ? ({ user: data.user, id: data.session.id } as any) : null,
		isLoaded: !isPending,
	};
}

export function useReverification<T extends (...args: any[]) => any>(action: T): T {
	return action;
}

export function SignInButton({
	children,
	forceRedirectUrl,
	signUpForceRedirectUrl,
}: ChildrenProps & { forceRedirectUrl?: string; signUpForceRedirectUrl?: string }) {
	return (
		<span onClick={() => redirectToSignIn(forceRedirectUrl ?? signUpForceRedirectUrl)}>
			{children}
		</span>
	);
}

export function SignOutButton({ children }: ChildrenProps) {
	return <span onClick={() => void endBrowserAndIdentitySession()}>{children}</span>;
}

export function Show({ when, children }: ChildrenProps & { when: 'signed-in' | 'signed-out' }) {
	const { data, isPending } = authClient.useSession();
	if (isPending) return null;
	return (when === 'signed-in') === !!data?.user ? <>{children}</> : null;
}

export function UserAvatar() {
	const { user } = useUser();
	return user?.imageUrl ? (
		<img className="size-8 rounded-full" src={user.imageUrl} alt="Account" />
	) : (
		<div className="size-8 rounded-full bg-muted" aria-label="Account" />
	);
}
